import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Outlet, useLocation, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import botService from '../services/botService';
import useAlert from '../hooks/useAlert';
import { ManageBotProvider } from '../contexts/ManageBotContext';
import './ManageBot.css';

const ManageBot = () => {
  const { botId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { alert, DialogComponent: AlertDialog } = useAlert();
  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (botId) {
      loadBot();
    } else {
      const storedBotId = localStorage.getItem('selectedBotId');
      if (storedBotId) {
        navigate(`/manage-bot/${storedBotId}/settings`, { replace: true });
      } else {
        setError(t('manage_bot.no_bot_selected'));
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botId, navigate, t]);

  useEffect(() => {
    // Redireciona para a primeira aba se estiver na rota base
    if (botId && location.pathname === `/manage-bot/${botId}`) {
      navigate(`/manage-bot/${botId}/settings`, { replace: true });
    }
  }, [botId, location.pathname, navigate]);

  const loadBot = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await botService.getBotById(botId);
      setBot(data);
      localStorage.setItem('selectedBotId', botId);
    } catch (err) {
      setError(err.response?.data?.error || t('manage_bot.error_loading_bot'));
      await alert(t('manage_bot.error_loading_bot_select_first'), t('common.error'), 'error');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Define as abas disponíveis
  const tabs = [
    { 
      id: 'settings', 
      label: t('manage_bot.tabs.settings'), 
      route: `/manage-bot/${botId}/settings`
    },
    { 
      id: 'welcome', 
      label: t('manage_bot.tabs.welcome_messages'), 
      route: `/manage-bot/${botId}/welcome`
    },
    { 
      id: 'payment-plans', 
      label: t('manage_bot.tabs.payment_plans'), 
      route: `/manage-bot/${botId}/payment-plans`
    },
    { 
      id: 'redirect', 
      label: t('manage_bot.tabs.redirect_buttons'), 
      route: `/manage-bot/${botId}/redirect`
    },
    { 
      id: 'commands', 
      label: t('manage_bot.tabs.commands'), 
      route: `/manage-bot/${botId}/commands`
    },
    { 
      id: 'administrators', 
      label: t('manage_bot.tabs.administrators'), 
      route: `/manage-bot/${botId}/administrators`
    },
    { 
      id: 'telegram-groups', 
      label: t('manage_bot.tabs.groups_and_channels'), 
      route: `/manage-bot/${botId}/telegram-groups`
    },
    { 
      id: 'botfather', 
      label: t('manage_bot.tabs.botfather'), 
      route: `/manage-bot/${botId}/botfather`
    },
    {
      id: 'warning-messages',
      label: t('manage_bot.tabs.warning_messages'),
      route: `/manage-bot/${botId}/warning-messages`
    },
    {
      id: 'admin-commands',
      label: t('manage_bot.tabs.admin_commands', 'Comandos Admin'),
      route: `/manage-bot/${botId}/admin-commands`
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="manage-bot-wrapper">
          <div className="manage-bot-container">
            <div className="loading">{t('manage_bot.loading_bot')}</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !bot) {
    return (
      <Layout>
        <div className="manage-bot-wrapper">
          <div className="manage-bot-container">
            <div className="error-container">
              <p>{error || t('manage_bot.bot_not_found')}</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <AlertDialog />
      <div className="manage-bot-wrapper">
        <div className="manage-bot-container">
          <div className="manage-bot-tabs">
            {tabs.map((tab) => (
              <NavLink
                key={tab.id}
                to={tab.route}
                className={({ isActive }) => `manage-bot-tab ${isActive ? 'active' : ''}`}
              >
                {tab.label}
              </NavLink>
            ))}
          </div>

          <div className="manage-bot-content">
            <ManageBotProvider>
              <Outlet />
            </ManageBotProvider>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ManageBot;

